import { useRef, useState } from 'react'
import InsertTokenMenu from '@/components/ui/InsertTokenMenu'
import RichTextEditor, { type RichTextEditorHandle } from '@/components/RichTextEditor'
import { MESSAGE_TOKENS, tokensInWords } from '@/features/messaging/tokens'
import { bodyForRichEditor, htmlToPlainText } from '@/lib/messageFormat'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { MessageTemplate, MessageTemplateInput } from '@/api/messages'
import Select from '@/components/ui/Select'
import ConfirmButton from '@/components/ui/ConfirmButton'
import { apiErrorMessage } from './apiErrorMessage'
import { useAllTemplates, useCreateTemplate, useDeleteTemplate, useUpdateTemplate } from './useTemplatesAdmin'

const inputCls =
  'px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

interface TemplateDraft {
  channel: 'email' | 'sms'
  name: string
  subject: string
  body: string
}

function draftFrom(t?: MessageTemplate): TemplateDraft {
  return {
    channel: t?.channel === 'sms' ? 'sms' : 'email',
    name: t?.name ?? '',
    subject: t?.subject ?? '',
    // Email bodies are edited in the rich editor (tokens as chips); old plain-text ones keep their breaks.
    body: t?.channel === 'sms' ? (t?.body ?? '') : bodyForRichEditor(t?.body ?? ''),
  }
}

function ChannelBadge({ channel }: { channel: string }) {
  const cls =
    channel === 'email' ? 'bg-brand-primary/10 text-brand-primary' : 'bg-success/15 text-success'
  return <span className={`text-xs rounded-full px-2 py-0.5 uppercase ${cls}`}>{channel}</span>
}

function TemplateEditor({
  initial,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial: TemplateDraft
  pending: boolean
  error: string | null
  onSave: (d: TemplateDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initial)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const editorRef = useRef<RichTextEditorHandle>(null)
  const isEmail = draft.channel === 'email'
  // agent_signature only renders in emails — hide it on SMS.
  const tokens = MESSAGE_TOKENS.filter((t) => isEmail || t !== '{{agent_signature}}')

  function insertToken(token: string) {
    if (isEmail) {
      editorRef.current?.insertText(token)
      return
    }
    const el = bodyRef.current
    const start = el?.selectionStart ?? draft.body.length
    const end = el?.selectionEnd ?? start
    setDraft((d) => ({ ...d, body: d.body.slice(0, start) + token + d.body.slice(end) }))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="px-4 py-3 bg-gray-50/60 border-t border-gray-100 space-y-2">
      <div className="flex items-center gap-2">
        <input
          className={`${inputCls} flex-1`}
          placeholder="Template name"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <Select
          value={draft.channel}
          onChange={(e) => {
            const channel = e.target.value as 'email' | 'sms'
            // Email is rich, SMS plain — carry the text across rather than show tags in an SMS.
            setDraft((d) => ({ ...d, channel, body: channel === 'sms' ? htmlToPlainText(d.body) : bodyForRichEditor(d.body) }))
          }}
        >
          <option value="email">email</option>
          <option value="sms">sms</option>
        </Select>
      </div>

      {isEmail && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject line</label>
          <input
            className={`${inputCls} w-full`}
            placeholder="Subject"
            value={draft.subject}
            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
          />
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">{isEmail ? 'Email body' : 'Message body'}</label>
        {isEmail ? (
          <RichTextEditor
            ref={editorRef}
            value={draft.body}
            onChange={(body) => setDraft((d) => ({ ...d, body }))}
            placeholder="Hi … — use the buttons below to add the client’s name and other details"
          />
        ) : (
          <textarea
            ref={bodyRef}
            className={`${inputCls} w-full min-h-32 text-sm leading-relaxed`}
            placeholder="Hi {{first_name}}, …"
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
          />
        )}
      </div>

      <div>
        {/* A template can be sent from a deal or straight to a contact: deal fields stay empty in the latter. */}
        <InsertTokenMenu tokens={tokens} onInsert={insertToken} customFields={['contact', 'deal']} />
        <p className="text-[11px] text-gray-400 mt-1">
          Personal details are filled in for each person the message goes to.
          {isEmail && ' Your signature is added at the end automatically, unless you place it with “My signature”.'}
        </p>
      </div>

      {error && <p className="text-danger text-xs">{error}</p>}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={pending || !draft.name.trim() || !draft.body.trim()}
          onClick={() => onSave(draft)}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          {pending ? 'Saving…' : 'Save template'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-500 hover:text-brand-dark">
          Cancel
        </button>
      </div>
    </div>
  )
}

export default function TemplatesTab() {
  const { data, isLoading } = useAllTemplates()
  const allTemplates = data ?? []
  const [query, setQuery] = useState('')
  const templates = allTemplates.filter((t) => matchesQuery(query, t.name, t.subject))
  const create = useCreateTemplate()
  const update = useUpdateTemplate()
  const del = useDeleteTemplate()

  // null = closed, 'new' = creating, otherwise the id being edited
  const [editing, setEditing] = useState<string | null>(null)

  function open(id: string | null) {
    create.reset()
    update.reset()
    setEditing(id)
  }

  function save(d: TemplateDraft) {
    const input: MessageTemplateInput = {
      channel: d.channel,
      name: d.name.trim(),
      subject: d.channel === 'email' ? d.subject.trim() || null : null,
      body: d.body,
    }
    if (editing === 'new') {
      create.mutate(input, { onSuccess: () => setEditing(null) })
    } else if (editing) {
      update.mutate({ id: editing, input }, { onSuccess: () => setEditing(null) })
    }
  }

  function remove(t: MessageTemplate) {
    del.mutate(t.id)
  }

  const saveError = create.error ?? update.error
  const delError = del.error

  return (
    <div className="max-w-5xl space-y-4">
      <p className="text-sm text-gray-500">
        Reusable email and SMS templates — available when sending from a deal, in bulk sends and in workflows.
      </p>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-brand-darkest">Templates</div>
          <button onClick={() => open('new')} className="text-xs text-brand-primary flex items-center gap-1 shrink-0">
            <Plus size={12} /> add template
          </button>
        </div>

        {(allTemplates.length > 5 || query) && (
          <div className="px-4 pb-3">
            <IndexToolbar query={query} onQueryChange={setQuery} placeholder="Search templates by name or subject…" shown={templates.length} total={allTemplates.length} />
          </div>
        )}
        {isLoading && <p className="px-4 pb-3 text-xs text-gray-400">Loading templates…</p>}
        {!isLoading && allTemplates.length === 0 && editing !== 'new' && (
          <p className="px-4 pb-3 text-xs text-gray-400">No templates yet.</p>
        )}
        {allTemplates.length > 0 && templates.length === 0 && (
          <p className="px-4 pb-3 text-xs text-gray-500">No templates match “{query}”.</p>
        )}

        {templates.map((t) =>
          editing === t.id ? (
            <TemplateEditor
              key={t.id}
              initial={draftFrom(t)}
              pending={update.isPending}
              error={saveError ? apiErrorMessage(saveError) : null}
              onSave={save}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div key={t.id} className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-brand-dark">{t.name}</div>
                {t.channel === 'email' && t.subject && (
                  <div className="text-xs text-gray-400 truncate">{t.subject && tokensInWords(t.subject)}</div>
                )}
              </div>
              <ChannelBadge channel={t.channel} />
              {t.fromTemplate && (
                <span
                  className="text-xs bg-brand-accent/20 text-brand-dark rounded-full px-2 py-0.5"
                  title="Seeded by the industry template"
                >
                  from template
                </span>
              )}
              <button onClick={() => open(t.id)} className="text-gray-400 hover:text-brand-primary">
                <Pencil size={14} />
              </button>
              <ConfirmButton onConfirm={() => remove(t)} title={`Delete template “${t.name}”`}>
                <Trash2 size={14} />
              </ConfirmButton>
            </div>
          ),
        )}

        {editing === 'new' && (
          <TemplateEditor
            initial={draftFrom()}
            pending={create.isPending}
            error={saveError ? apiErrorMessage(saveError) : null}
            onSave={save}
            onCancel={() => setEditing(null)}
          />
        )}

        {delError && <p className="px-4 py-2 text-danger text-xs border-t border-gray-100">{apiErrorMessage(delError)}</p>}
      </div>
    </div>
  )
}
